import {
  Text,
  Flex,
  NumberInput,
  Select,
  Loader,
  type SelectProps,
  type NumberInputProps,
  type FlexProps,
} from '@mantine/core';
import classes from './formSalarySelectField.module.scss';
import { useGetPaymentPeriod } from '@entities/paymentPeriod';
import { useGetCurrency } from '@entities/currency';
import { useMemo } from 'react';
import { IconChevronDown } from '@tabler/icons-react';

interface FormSalarySelectFieldProps {
  title: string;
  isSalaryRange?: boolean;
  description: string;
  containerProps?: FlexProps;
  salaryFrom?: string;
  salaryTo?: string;
  salary?: string;
  currencyId?: string;
  paymentPeriodId?: string;
  onSalaryChange?: (value: number | string) => void;
  onSalaryFromChange?: (value: number | string) => void;
  onSalaryToChange?: (value: number | string) => void;
  onCurrencyChange?: (value: string | null) => void;
  onPaymentPeriodChange?: (value: string | null) => void;
  propsFromInput?: NumberInputProps;
  propsToInput?: NumberInputProps;
  propsSalaryInput?: NumberInputProps;
  propsCurrencyInput?: SelectProps;
  propsPaymentPeriodInput?: SelectProps;
}

export const FormSalarySelectField = ({
  title,
  isSalaryRange = true,
  description,
  containerProps,
  salaryFrom,
  salaryTo,
  salary,
  currencyId,
  paymentPeriodId,
  onSalaryFromChange,
  onSalaryToChange,
  onSalaryChange,
  onCurrencyChange,
  onPaymentPeriodChange,
  propsFromInput,
  propsToInput,
  propsCurrencyInput,
  propsPaymentPeriodInput,
  propsSalaryInput,
}: FormSalarySelectFieldProps) => {
  const { data: paymentPeriods, isLoading: isPeriodLoading } =
    useGetPaymentPeriod();
  const paymentPeriodsOptions = useMemo(
    () =>
      paymentPeriods?.map(period => ({
        label: period.name,
        value: period.id.toString(),
      })),
    [paymentPeriods?.length]
  );

  const { data: currencies, isLoading: isCurrencyLoading } = useGetCurrency();
  const currenciesOptions = useMemo(
    () =>
      currencies?.map(currency => ({
        label: currency.code,
        value: currency.id.toString(),
      })),
    [currencies?.length]
  );

  return (
    <Flex gap='24' className={classes.formRow} {...containerProps}>
      <Flex direction='column' gap={6} className={classes.leftColumn}>
        <Text className={classes.title}>{title}</Text>
        <Text className={classes.description}>{description}</Text>
      </Flex>

      <Flex className={classes.rightColumn} gap={16} wrap='wrap'>
        {isSalaryRange ? (
          <>
            <NumberInput
              placeholder='От'
              variant='filled'
              w={133}
              value={salaryFrom}
              onChange={onSalaryFromChange}
              {...propsFromInput}
            />
            <NumberInput
              placeholder='До'
              variant='filled'
              w={133}
              value={salaryTo}
              onChange={onSalaryToChange}
              {...propsToInput}
            />
          </>
        ) : (
          <NumberInput
            variant='filled'
            w={133}
            value={salary}
            onChange={onSalaryChange}
            {...propsSalaryInput}
          />
        )}

        <Select
          data={currenciesOptions}
          placeholder='Валюта'
          variant='filled'
          w={100}
          value={currencyId}
          onChange={onCurrencyChange}
          rightSection={
            isCurrencyLoading ? (
              <Loader size='xs' />
            ) : (
              <IconChevronDown
                size={20}
                color='var(--mantine-color-primary-7)'
              />
            )
          }
          searchable
          {...propsCurrencyInput}
        />
        <Select
          data={paymentPeriodsOptions}
          placeholder='Период'
          variant='filled'
          w={133}
          value={paymentPeriodId}
          onChange={onPaymentPeriodChange}
          rightSection={
            isPeriodLoading ? (
              <Loader size='xs' />
            ) : (
              <IconChevronDown
                size={20}
                color='var(--mantine-color-primary-7)'
              />
            )
          }
          searchable
          {...propsPaymentPeriodInput}
        />
      </Flex>
    </Flex>
  );
};
